import { useEffect, useRef } from 'react';
import { Theme, Box } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { DocumentTypes } from 'types';
import PDFTronManager from 'components/PDFViewer/PDFTronManager';
import { inspection, getSyncScrolling, getDocumentsState, getSinglePageView, getCurrentPage } from 'store';
import { GVToggleButton } from 'components/common';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import { makeStyles } from 'tss-react/mui';
import SyncScrollingIcon from 'components/icons/SyncScrollingIcon/SyncScrollingIcon';
import store from 'store/store';

const useStyles = makeStyles()((theme: Theme) => ({
  button: {
    '&.MuiToggleButton-root': {
      display: 'flex',
      height: '31px',
      padding: '7px 8px 8px 8px',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '4px',
      border: '1px solid var(--gds-color-cool-gray-900, #101113)',
      width: '31px',
      minWidth: '31px',
      gap: '0',
      margin: '0 16px',
      '@container gv-toolbar (min-width: 1500px)': {
        width: '65px',
        minWidth: '65px',
        gap: '6px',
      },
    },
  },
  syncText: {
    display: 'none',
    '@container gv-toolbar (min-width: 1500px)': {
      display: 'inline',
    },
  },
  activeButton: {
    backgroundColor: '#EA3632',
    '&:hover': {
      backgroundColor: '#EA3632',
    },
  },
  inactiveButton: {
    background: 'var(--gds-color-cool-gray-500, #292B30)',
    '&:hover': {
      backgroundColor: 'var(--gds-color-cool-gray-450)',
    },
  },
}));

let sourceOldCoords = { x: 0, y: 0, page: 1 };
let targetOldCoords = { x: 0, y: 0, page: 1 };
let originalScrollPositionDiff = { x: 0, y: 0 }; // difference between source/target scroll position when sync is enabled
let targetDocAhead = { x: false, y: false }; // used in syncScroll() to determine the scroll behavior when one/both documents have reached the start/end of document
let isMirroringPageChange = false; // guard to prevent feedback loops when mirroring page changes

export const updatePreviousScrollPostion = (
  scrollElementSource?: Element,
  scrollElementTarget?: Element,
  sourceCurrentPage?: number,
  targetCurrentPage?: number,
) => {
  if (scrollElementSource) {
    sourceOldCoords = {
      x: scrollElementSource.scrollLeft,
      y: scrollElementSource.scrollTop,
      page: sourceOldCoords.page,
    };
    if (sourceCurrentPage) {
      sourceOldCoords.page = sourceCurrentPage;
    }
  }
  if (scrollElementTarget) {
    targetOldCoords = {
      x: scrollElementTarget.scrollLeft,
      y: scrollElementTarget.scrollTop,
      page: targetOldCoords.page,
    };
    if (targetCurrentPage) {
      targetOldCoords.page = targetCurrentPage;
    }
  }
};

const SyncScrollingButton = () => {
  const dispatch = useDispatch();
  const { classes, cx } = useStyles();
  const syncScrolling = useSelector(getSyncScrolling);
  const singlePageView = useSelector(getSinglePageView);
  // Mirror based on Redux currentPage changes (covers fit-to-page and programmatic page changes)
  const prevSourcePageRef = useRef<number | null>(null);
  const prevTargetPageRef = useRef<number | null>(null);

  const { source: documentStateSource, target: documentStateTarget } = useSelector(getDocumentsState);
  const { source: sourceCurrentPage, target: targetCurrentPage } = useSelector(getCurrentPage);

  const scrollElementSource = PDFTronManager.getScrollElement(DocumentTypes.source);
  const scrollElementTarget = PDFTronManager.getScrollElement(DocumentTypes.target);

  const updateTargetDocAhead = () => {
    if (scrollElementSource && scrollElementTarget) {
      targetDocAhead.x =
        sourceOldCoords.x / scrollElementSource.scrollWidth < targetOldCoords.x / scrollElementTarget.scrollWidth;
      targetDocAhead.y =
        sourceOldCoords.y / scrollElementSource.scrollHeight < targetOldCoords.y / scrollElementTarget.scrollHeight;
    }
  };

  const endOfDocReached = (axis: 'x' | 'y', scrollElement: Element): boolean => {
    if (axis === 'x') {
      const xCoord = scrollElement === scrollElementSource ? sourceOldCoords.x : targetOldCoords.x;
      return xCoord <= 1 || Math.abs(scrollElement.scrollWidth - scrollElement.clientWidth - xCoord) <= 1;
    } else {
      const yCoord = scrollElement === scrollElementSource ? sourceOldCoords.y : targetOldCoords.y;
      return yCoord <= 1 || Math.abs(scrollElement.scrollHeight - scrollElement.clientHeight - yCoord) <= 1;
    }
  };

  const toggleSync = () => {
    if (!syncScrolling && scrollElementSource && scrollElementTarget) {
      updatePreviousScrollPostion(scrollElementSource, scrollElementTarget, sourceCurrentPage, targetCurrentPage);
      originalScrollPositionDiff.y = scrollElementTarget.scrollTop - scrollElementSource.scrollTop;
      originalScrollPositionDiff.x = scrollElementTarget.scrollLeft - scrollElementTarget.scrollLeft;
      updateTargetDocAhead();
    }
    prevSourcePageRef.current = null;
    prevTargetPageRef.current = null;
    dispatch(inspection.actions.toggleSyncScrolling());
  };

  const syncScrollYSinglePage = (
    scrolledDocument: { scrollElement: Element; oldCoords: { x: number; y: number; page: number } },
    oppositeDocument: { scrollElement: Element; oldCoords: { x: number; y: number; page: number } },
    documentType: DocumentTypes,
  ) => {
    const diff = scrolledDocument.scrollElement['scrollTop'] - scrolledDocument.oldCoords['y'];

    // we need to call this from the store because this function is called inside the event listener with the old state
    const { source: currentStorePageSource, target: currentStorePageTarget } = getCurrentPage(store.getState());
    const currentStorePage = documentType === DocumentTypes.source ? currentStorePageSource : currentStorePageTarget;

    // If a page change just occurred skip vertical mirroring
    if (scrolledDocument.oldCoords.page !== currentStorePage) {
      updatePreviousScrollPostion(
        scrollElementSource,
        scrollElementTarget,
        currentStorePageSource,
        currentStorePageTarget,
      );
      return;
    }
    const oppEl = oppositeDocument.scrollElement;
    const oppScrollTop = oppEl.scrollTop;
    const oppScrollHeight = oppEl.scrollHeight;
    const oppClientHeight = oppEl.clientHeight;
    const atTop = oppScrollTop <= 1;
    const atBottom = Math.abs(oppScrollHeight - oppClientHeight - oppScrollTop) <= 1;
    // If the document is at the top or bottom of the page, skip vertical mirroring
    if ((diff < 0 && atTop) || (diff > 0 && atBottom)) {
      updatePreviousScrollPostion(
        scrollElementSource,
        scrollElementTarget,
        currentStorePageSource,
        currentStorePageTarget,
      );
      return;
    }

    // Scroll the opposite document the same amount as the scrolled document
    oppositeDocument.scrollElement['scrollTop'] = oppositeDocument.oldCoords['y'] + diff;
    updatePreviousScrollPostion(
      scrollElementSource,
      scrollElementTarget,
      currentStorePageSource,
      currentStorePageTarget,
    );
    return;
  };

  const syncScroll = (
    axis: 'x' | 'y',
    scrolledDocument: { scrollElement: Element; oldCoords: { x: number; y: number; page: number } },
    oppositeDocument: { scrollElement: Element; oldCoords: { x: number; y: number; page: number } },
  ) => {
    if (scrollElementSource && scrollElementTarget) {
      const scrollProp = axis === 'x' ? 'scrollLeft' : 'scrollTop';
      const diff = scrolledDocument.scrollElement[scrollProp] - scrolledDocument.oldCoords[axis];
      // if a longer document is scrolling to an edge after the other document has already reached it we don't want to update targetDocAhead
      if (!(endOfDocReached(axis, scrollElementSource) || endOfDocReached(axis, scrollElementTarget))) {
        updateTargetDocAhead();
      }

      const currentScrollPositionDiff = targetOldCoords[axis] - sourceOldCoords[axis];

      if (
        (targetDocAhead[axis] && currentScrollPositionDiff >= originalScrollPositionDiff[axis]) ||
        (!targetDocAhead[axis] && currentScrollPositionDiff <= originalScrollPositionDiff[axis])
      ) {
        // if we aren't scrolling into the start/end of either doc, we scroll the opposite doc the same amount as the scrolled doc
        oppositeDocument.scrollElement[scrollProp] = oppositeDocument.oldCoords[axis] + diff;
      } else if (
        (targetDocAhead[axis] && currentScrollPositionDiff < originalScrollPositionDiff[axis]) ||
        (!targetDocAhead[axis] && currentScrollPositionDiff > originalScrollPositionDiff[axis])
      ) {
        // when the user is scrolling away from the start/end of either doc and we need to restore the space between their scroll positions
        oppositeDocument.scrollElement[scrollProp] =
          scrolledDocument.scrollElement === scrollElementTarget
            ? scrolledDocument.scrollElement[scrollProp] - originalScrollPositionDiff[axis]
            : scrolledDocument.scrollElement[scrollProp] + originalScrollPositionDiff[axis];
      }

      updatePreviousScrollPostion(scrollElementSource, scrollElementTarget);
    }
  };

  useEffect(() => {
    const scrollTargetDocument = () => {
      // if it's not sync scrolling, we don't need to scroll the target document
      if (!syncScrolling || !scrollElementTarget || !scrollElementSource) return;

      const scrolledDocument = { scrollElement: scrollElementSource, oldCoords: sourceOldCoords };
      const oppositeDocument = { scrollElement: scrollElementTarget, oldCoords: targetOldCoords };

      if (scrolledDocument.scrollElement.scrollLeft !== scrolledDocument.oldCoords.x) {
        syncScroll('x', scrolledDocument, oppositeDocument);
      }
      if (scrolledDocument.scrollElement.scrollTop !== scrolledDocument.oldCoords.y) {
        if (singlePageView) {
          syncScrollYSinglePage(scrolledDocument, oppositeDocument, DocumentTypes.source);
        } else {
          syncScroll('y', scrolledDocument, oppositeDocument);
        }
      }
    };
    const scrollSourceDocument = () => {
      // if it's not sync scrolling, we don't need to scroll the source documen
      if (!syncScrolling || !scrollElementTarget || !scrollElementSource) return;
      const scrolledDocument = { scrollElement: scrollElementTarget, oldCoords: targetOldCoords };
      const oppositeDocument = { scrollElement: scrollElementSource, oldCoords: sourceOldCoords };

      if (scrolledDocument.scrollElement.scrollLeft !== scrolledDocument.oldCoords.x) {
        syncScroll('x', scrolledDocument, oppositeDocument);
      }
      if (scrolledDocument.scrollElement.scrollTop !== scrolledDocument.oldCoords.y) {
        if (singlePageView) {
          syncScrollYSinglePage(scrolledDocument, oppositeDocument, DocumentTypes.target);
        } else {
          syncScroll('y', scrolledDocument, oppositeDocument);
        }
      }
    };

    if (scrollElementSource && scrollElementTarget && syncScrolling) {
      scrollElementSource.addEventListener('scroll', scrollTargetDocument);
      scrollElementTarget.addEventListener('scroll', scrollSourceDocument);
    }
    return () => {
      if (scrollElementSource && scrollElementTarget) {
        scrollElementSource.removeEventListener('scroll', scrollTargetDocument);
        scrollElementTarget.removeEventListener('scroll', scrollSourceDocument);
      }
    };
  }, [syncScrolling, documentStateSource, documentStateTarget, singlePageView]);

  useEffect(() => {
    if (!syncScrolling || !singlePageView) return;
    const sourceInstance = PDFTronManager.getPDFInstance(DocumentTypes.source);
    const targetInstance = PDFTronManager.getPDFInstance(DocumentTypes.target);
    const sourceDV = sourceInstance?.Core.documentViewer;
    const targetDV = targetInstance?.Core.documentViewer;
    if (!sourceDV || !targetDV) return;

    // Initialize refs on first run
    if (prevSourcePageRef.current === null || prevTargetPageRef.current === null) {
      prevSourcePageRef.current = sourceCurrentPage;
      prevTargetPageRef.current = targetCurrentPage;
      return;
    }

    const sourceChanged = sourceCurrentPage !== prevSourcePageRef.current;
    const targetChanged = targetCurrentPage !== prevTargetPageRef.current;

    if (isMirroringPageChange) {
      isMirroringPageChange = false;
    } else {
      if (sourceChanged) {
        const delta = sourceCurrentPage - prevSourcePageRef.current;
        if (delta !== 0) {
          const max = targetDV.getPageCount();
          const next = targetCurrentPage + delta;
          if (next >= 1 && next <= max) {
            isMirroringPageChange = true;
            targetDV.setCurrentPage(next, false);
            // If we navigated upward (to previous page), keep opposite at bottom
            if (delta < 0) {
              const oppEl = scrollElementTarget || PDFTronManager.getScrollElement(DocumentTypes.target);
              if (oppEl) {
                oppEl.scrollTop = oppEl.scrollHeight - oppEl.clientHeight;
                updatePreviousScrollPostion(undefined, oppEl, undefined, next);
              }
            }
          }
        }
      } else if (targetChanged) {
        const delta = targetCurrentPage - prevTargetPageRef.current;
        if (delta !== 0) {
          const max = sourceDV.getPageCount();
          const next = sourceCurrentPage + delta;
          if (next >= 1 && next <= max) {
            isMirroringPageChange = true;
            sourceDV.setCurrentPage(next, false);
            // If we navigated upward (to previous page), keep opposite at bottom
            if (delta < 0) {
              const oppEl = scrollElementSource || PDFTronManager.getScrollElement(DocumentTypes.source);
              if (oppEl) {
                oppEl.scrollTop = oppEl.scrollHeight - oppEl.clientHeight;
                updatePreviousScrollPostion(oppEl, undefined, next, undefined);
              }
            }
          }
        }
      }
    }

    prevSourcePageRef.current = sourceCurrentPage;
    prevTargetPageRef.current = targetCurrentPage;
  }, [sourceCurrentPage, targetCurrentPage, syncScrolling, singlePageView]);

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <GVTooltip title="Sync Scrolling" side="top">
        <GVToggleButton
          className={cx(classes.button, syncScrolling ? classes.activeButton : classes.inactiveButton)}
          value="syncScroll"
          id="SyncScrollButton"
          selected={syncScrolling}
          onChange={toggleSync}
          textButton
          style={{
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
            zIndex: 10,
          }}
        >
          <SyncScrollingIcon />
          <span className={classes.syncText}>Sync</span>
        </GVToggleButton>
      </GVTooltip>
    </Box>
  );
};

export default SyncScrollingButton;
